/**
 * Training Platform Page
 * AIDLC Administrative Backend
 *
 * Interactive training platform for onboarding new team members.
 * Managed by Maureen (Trainer agent). Features module cards with
 * progress tracking, rich lesson content, architecture diagrams,
 * code snippets, expandable sections, and quizzes.
 *
 * Progress persisted in localStorage under 'aidlc-training-progress'.
 *
 * Personas: All (visible to every persona)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

// ─── Types ────────────────────────────────────────────────────────

interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

interface LessonSection {
  id: string;
  title: string;
  content: string; // HTML string
}

interface TrainingModule {
  id: string;
  title: string;
  description: string;
  icon: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  estimatedMinutes: number;
  sections: LessonSection[];
  quiz: QuizQuestion[];
}

interface TrainingProgress {
  completedSections: Record<string, string[]>; // moduleId -> sectionId[]
  quizScores: Record<string, number>;          // moduleId -> best score
}

// ─── localStorage helpers ─────────────────────────────────────────

const STORAGE_KEY = 'aidlc-training-progress';

function loadProgress(): TrainingProgress {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* silent */ }
  return { completedSections: {}, quizScores: {} };
}

function saveProgress(p: TrainingProgress) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch { /* silent */ }
}

// ─── Module Data ──────────────────────────────────────────────────

const MODULES: TrainingModule[] = [
  // ── MODULE 1: Welcome to AIDLC ──
  {
    id: 'welcome',
    title: 'Welcome to AIDLC',
    description: 'Learn what AIDLC is, how the repositories are structured, and how the entire system works at a high level.',
    icon: '\u{1F3E2}',
    difficulty: 'Beginner',
    estimatedMinutes: 15,
    sections: [
      {
        id: 'what-is-aidlc',
        title: 'What is AIDLC?',
        content: `
<h2>What is AIDLC?</h2>
<p><strong>AIDLC</strong> stands for <em>AI Development Lifecycle</em>. It is LDAJA's framework for managing software development using a team of specialized AI agents, each with a defined persona and area of expertise.</p>

<div class="callout info">
  <strong>Key Insight:</strong> AIDLC is not just tooling — it is an entire operational model where AI agents collaborate with human leadership to plan, build, review, test, and ship software.
</div>

<p>The system is managed through the <strong>AIDLC Admin Portal</strong>, a real-time dashboard that lets the CEO and Operations Director monitor all agent activity, review work output, manage sprints, and enforce quality gates.</p>

<h3>Core Principles</h3>
<ul>
  <li><strong>Specialization:</strong> Each agent has a focused role — no one agent does everything.</li>
  <li><strong>Process Enforcement:</strong> A strict Definition of Done (DoD) pipeline ensures quality at every step.</li>
  <li><strong>Transparency:</strong> All agent work is logged, tracked in the backlog, and visible on the admin portal.</li>
  <li><strong>Human Oversight:</strong> The CEO approves budgets, reviews output, and sets direction. Agents execute.</li>
</ul>

<h3>What Makes AIDLC Different?</h3>
<p>Traditional dev teams have one or two developers juggling many hats. AIDLC gives each responsibility to a dedicated agent:</p>

<table>
  <thead><tr><th>Traditional</th><th>AIDLC</th></tr></thead>
  <tbody>
    <tr><td>Dev writes code + tests</td><td>Marcus writes code, Asha tests independently</td></tr>
    <tr><td>Self-review or peer review</td><td>Bill reviews every change with strict criteria</td></tr>
    <tr><td>Ad-hoc planning</td><td>Sam manages sprints with structured ceremonies</td></tr>
    <tr><td>Manual compliance checks</td><td>Sarah reviews privacy/compliance automatically</td></tr>
  </tbody>
</table>
`,
      },
      {
        id: 'monorepo-structure',
        title: 'The Repository Structure',
        content: `
<h2>The Repository Structure</h2>
<p>LDAJA code lives in five GitHub repositories under <code>github.com/ldaja-ai</code>, checked out side by side in one parent folder. They replaced the earlier single repository:</p>

<div class="code-block">
<pre>ldaja/                    # any parent folder
\u251C\u2500\u2500 aidlc-portal/         # AIDLC Admin Portal (this application)
\u2502   \u251C\u2500\u2500 src/              # React + TypeScript + Vite frontend
\u2502   \u251C\u2500\u2500 server/           # Express + Socket.io backend
\u2502   \u2514\u2500\u2500 bridge/           # File-based JSON IPC layer
\u251C\u2500\u2500 crm/                  # LDAJA CRM
\u251C\u2500\u2500 compliance-center/    # HIPAA + SOC Compliance Center
\u251C\u2500\u2500 rink-rater/           # Youth hockey team management app
\u2502   \u251C\u2500\u2500 screens/          # React Native screens
\u2502   \u251C\u2500\u2500 services/         # Firebase services
\u2502   \u2514\u2500\u2500 components/       # Shared UI components
\u2514\u2500\u2500 handbook/             # Company process and documentation
    \u251C\u2500\u2500 docs/             # Documentation, reports, AgentHub
    \u2514\u2500\u2500 CLAUDE.md         # Master instructions for all agents</pre>
</div>

<div class="callout tip">
  <strong>Tip:</strong> The <code>CLAUDE.md</code> file in the <code>handbook</code> repository is the single source of truth for all agent behavior, team roles, and process rules. Each product repository has a short <code>CLAUDE.md</code> that points to it and adds that product&#39;s own facts.
</div>

<h3>Project Breakdown</h3>

<div class="expandable" data-title="aidlc-portal/ — The Admin Portal">
  <p>The admin portal is the command center for the entire operation. It has three layers:</p>
  <ul>
    <li><strong>Frontend</strong> (React + Vite + Tailwind): Dashboard, sprint board, backlog, agent monitoring, chat interface, and more.</li>
    <li><strong>Backend</strong> (Express + Socket.io): REST API with 48+ endpoints, real-time WebSocket updates, in-memory store with disk persistence.</li>
    <li><strong>Bridge</strong> (JSON files): File-based IPC that connects CLI-based agents to the web-based portal.</li>
  </ul>
</div>

<div class="expandable" data-title="rink-rater/ — The Hockey App">
  <p>Rink Rater is a React Native/Expo mobile app for youth hockey team management. Features include:</p>
  <ul>
    <li>Player roster with position tracking (C, LW, RW, D, G)</li>
    <li>Game scheduling and attendance</li>
    <li>Player performance metrics</li>
    <li>Firebase backend for data storage</li>
  </ul>
</div>

<div class="expandable" data-title="handbook/docs/ — Documentation">
  <p>Contains generated reports, the AgentHub communications dashboard, load test results, and architecture documents.</p>
</div>
`,
      },
      {
        id: 'how-it-works',
        title: 'How the System Works',
        content: `
<h2>How the System Works</h2>
<p>At a high level, the AIDLC system operates as a continuous loop of <strong>Plan \u2192 Build \u2192 Review \u2192 Ship</strong>.</p>

<div class="diagram">
  <div class="diagram-row">
    <div class="diagram-box blue">CEO<br/><span class="small">Sets direction</span></div>
    <div class="diagram-arrow">\u2192</div>
    <div class="diagram-box purple">Sam (Scrum)<br/><span class="small">Plans sprints</span></div>
    <div class="diagram-arrow">\u2192</div>
    <div class="diagram-box green">Marcus (Dev)<br/><span class="small">Builds features</span></div>
  </div>
  <div class="diagram-row">
    <div class="diagram-box green">Marcus (Dev)<br/><span class="small">Code complete</span></div>
    <div class="diagram-arrow">\u2192</div>
    <div class="diagram-box orange">Bill (Review)<br/><span class="small">Code review +2</span></div>
    <div class="diagram-arrow">\u2192</div>
    <div class="diagram-box orange">Asha (QA)<br/><span class="small">Test & verify</span></div>
  </div>
  <div class="diagram-row">
    <div class="diagram-box orange">Asha (QA)<br/><span class="small">PASS</span></div>
    <div class="diagram-arrow">\u2192</div>
    <div class="diagram-box indigo">Dan (Ops)<br/><span class="small">Commit & push</span></div>
    <div class="diagram-arrow">\u2192</div>
    <div class="diagram-box blue">CEO<br/><span class="small">Reviews output</span></div>
  </div>
</div>

<h3>The Definition of Done (DoD) Pipeline</h3>
<p>This is the <strong>#1 rule</strong> in the entire repository. No code change ships without completing all steps in order:</p>

<ol>
  <li><strong>Code Complete</strong> — Developer finishes implementation, code compiles, no type errors.</li>
  <li><strong>Bill Code Review</strong> — Bill reviews for code smells, security, clean code. Must receive <code>+2</code>.</li>
  <li><strong>Asha QA Review</strong> — Asha tests full user flows, edge cases, schema mismatches. Must receive <code>PASS</code>.</li>
  <li><strong>Commit and Push</strong> — Only after both approvals. Commit includes review trailers.</li>
</ol>

<div class="callout warning">
  <strong>No Exceptions:</strong> Even a one-line fix or a type change must go through the full DoD pipeline. The CEO expects this process every time, without shortcuts.
</div>

<h3>Commit Message Format</h3>
<div class="code-block">
<pre>Short summary of what changed

Optional longer description

Reviewed-by: Bill Torres (+2)
QA-verified-by: Asha Patel (PASS)
Co-Authored-By: Claude Opus 4.6 &lt;noreply@anthropic.com&gt;</pre>
</div>
`,
      },
      {
        id: 'architecture-diagram',
        title: 'Architecture Overview',
        content: `
<h2>Architecture Overview</h2>
<p>The complete AIDLC system architecture connects multiple layers:</p>

<div class="arch-diagram">
  <div class="arch-layer" style="border-color: #6366f1">
    <div class="arch-label" style="background: #6366f1">Presentation Layer</div>
    <div class="arch-items">
      <div class="arch-item">AIDLC Admin Portal<br/><span class="small">React + Vite + Tailwind</span></div>
      <div class="arch-item">Rink Rater App<br/><span class="small">React Native + Expo</span></div>
    </div>
  </div>
  <div class="arch-connector">\u2195</div>
  <div class="arch-layer" style="border-color: #22c55e">
    <div class="arch-label" style="background: #22c55e">API & Real-Time Layer</div>
    <div class="arch-items">
      <div class="arch-item">Express REST API<br/><span class="small">48+ endpoints</span></div>
      <div class="arch-item">Socket.io<br/><span class="small">WebSocket push</span></div>
      <div class="arch-item">Bridge Watcher<br/><span class="small">1s file polling</span></div>
    </div>
  </div>
  <div class="arch-connector">\u2195</div>
  <div class="arch-layer" style="border-color: #f59e0b">
    <div class="arch-label" style="background: #f59e0b; color: #000">Data & Persistence Layer</div>
    <div class="arch-items">
      <div class="arch-item">In-Memory Store<br/><span class="small">store.ts (~4600 lines)</span></div>
      <div class="arch-item">Disk Persistence<br/><span class="small">JSON file archives</span></div>
      <div class="arch-item">Firebase<br/><span class="small">Rink Rater data</span></div>
    </div>
  </div>
  <div class="arch-connector">\u2195</div>
  <div class="arch-layer" style="border-color: #ef4444">
    <div class="arch-label" style="background: #ef4444">Agent Layer</div>
    <div class="arch-items">
      <div class="arch-item">CLI Agents<br/><span class="small">Claude Code sessions</span></div>
      <div class="arch-item">Bridge Files<br/><span class="small">JSON IPC</span></div>
      <div class="arch-item">Scheduler Engine<br/><span class="small">Recursive setTimeout</span></div>
    </div>
  </div>
</div>
`,
      },
    ],
    quiz: [
      {
        question: 'What does AIDLC stand for?',
        options: [
          'Automated Infrastructure Deployment Lifecycle',
          'AI Development Lifecycle',
          'Agent-Integrated DevOps Lifecycle',
          'Advanced Interface Design Lifecycle',
        ],
        correctIndex: 1,
        explanation: 'AIDLC stands for AI Development Lifecycle — LDAJA\'s framework for managing software development with specialized AI agents.',
      },
      {
        question: 'In the DoD pipeline, what must happen BEFORE Asha runs QA?',
        options: [
          'The CEO must approve the feature',
          'Sam must plan the sprint',
          'Bill must give a +2 code review',
          'The code must be deployed to staging',
        ],
        correctIndex: 2,
        explanation: 'The DoD pipeline order is strict: Code Complete -> Bill Code Review (+2) -> Asha QA (PASS) -> Commit. Bill must approve before Asha tests.',
      },
      {
        question: 'Where is the single source of truth for all agent behavior and process rules?',
        options: [
          'The AIDLC Admin Portal settings page',
          'The Firebase database',
          'CLAUDE.md at the repo root',
          'Each agent\'s individual config file',
        ],
        correctIndex: 2,
        explanation: 'CLAUDE.md at the repository root is the master instruction file that defines all agent roles, process rules, and the Definition of Done pipeline.',
      },
    ],
  },

  // ── MODULE 2: Meet the Team ──
  {
    id: 'team',
    title: 'Meet the Team',
    description: 'Get to know every agent on the AIDLC team, their roles, personalities, and when to invoke each one.',
    icon: '\u{1F465}',
    difficulty: 'Beginner',
    estimatedMinutes: 20,
    sections: [
      {
        id: 'agent-roster',
        title: 'Agent Roster',
        content: `
<h2>The AIDLC Agent Roster</h2>
<p>The AIDLC team consists of specialized agents, each with a distinct persona, expertise, and personality. Here is the complete roster:</p>

<table class="agent-table">
  <thead>
    <tr><th>Agent</th><th>Persona</th><th>Role</th><th>Experience</th></tr>
  </thead>
  <tbody>
    <tr><td><strong>Dan</strong></td><td>Dan</td><td>Director of Operations</td><td>25 yrs</td></tr>
    <tr><td><strong>Marcus</strong></td><td>Marcus "Dev" Chen</td><td>Senior Full-Stack Developer</td><td>12 yrs</td></tr>
    <tr><td><strong>Bill</strong></td><td>Bill "The Gatekeeper" Torres</td><td>Principal Code Reviewer &amp; OWASP SME</td><td>20 yrs</td></tr>
    <tr><td><strong>Asha</strong></td><td>Asha "Bug Hunter" Patel</td><td>QA Test Engineer &amp; Playwright SME</td><td>8 yrs</td></tr>
    <tr><td><strong>Todd</strong></td><td>Todd "Ping" Harrison</td><td>Network Administrator</td><td>15 yrs</td></tr>
    <tr><td><strong>Raj</strong></td><td>Raj "Query" Vikram</td><td>Database Architect</td><td>18 yrs</td></tr>
    <tr><td><strong>Maya</strong></td><td>Maya "Pixel" Rodriguez</td><td>UI/UX Architect</td><td>10 yrs</td></tr>
    <tr><td><strong>Sarah</strong></td><td>Sarah "Compliance" Kim</td><td>Compliance &amp; Legal Advisor</td><td>14 yrs</td></tr>
    <tr><td><strong>Elena</strong></td><td>Elena "Docs" Vasquez</td><td>Technical Writer</td><td>9 yrs</td></tr>
    <tr><td><strong>Carlos</strong></td><td>Carlos "Mobile" Rivera</td><td>Mobile Developer</td><td>11 yrs</td></tr>
    <tr><td><strong>Priya</strong></td><td>Priya "Scale" Sharma</td><td>Performance Engineer</td><td>7 yrs</td></tr>
    <tr><td><strong>James</strong></td><td>James "DevOps" Okafor</td><td>DevOps Engineer</td><td>10 yrs</td></tr>
    <tr><td><strong>Mei</strong></td><td>Mei "Analytics" Wong</td><td>Data Analyst</td><td>6 yrs</td></tr>
    <tr><td><strong>Harper</strong></td><td>Harper "Culture" Wells</td><td>HR Director &amp; Talent Acquisition</td><td>18 yrs</td></tr>
    <tr><td><strong>Alex</strong></td><td>Alex "Architect" Thompson</td><td>Solutions Architect</td><td>16 yrs</td></tr>
    <tr><td><strong>Sam</strong></td><td>Sam "Sprint" Kennedy</td><td>Scrum Master</td><td>7 yrs</td></tr>
    <tr><td><strong>Maureen</strong></td><td>Maureen "The Teacher" Calloway</td><td>Technical Trainer &amp; Documentation Specialist</td><td>14 yrs</td></tr>
    <tr><td><strong>Bedsy</strong></td><td>Bedsy</td><td>Hockey Domain Consultant</td><td>25 yrs</td></tr>
    <tr><td><strong>Gregory</strong></td><td>Gregory "The Guardian" Jameson III</td><td>HIPAA &amp; SOC Compliance Auditor</td><td>22 yrs</td></tr>
    <tr><td><strong>Nina</strong></td><td>Nina "The Closer" Castellanos</td><td>Head of Marketing</td><td>15 yrs</td></tr>
    <tr><td><strong>Jamie</strong></td><td>James "Jamie" Sullivan</td><td>Marketing Copy Writer</td><td>13 yrs</td></tr>
    <tr><td><strong>Kim</strong></td><td>Kim "The Interviewer" Ashford</td><td>Product Manager</td><td>14 yrs</td></tr>
  </tbody>
</table>

<div class="callout info">
  <strong>Fun Fact:</strong> Each agent has a unique catchphrase that reflects their personality and priorities.
</div>
`,
      },
      {
        id: 'when-to-invoke',
        title: 'When to Invoke Each Agent',
        content: `
<h2>When to Invoke Each Agent</h2>
<p>Agents should be spawned <strong>automatically</strong> based on the type of work being done. The orchestrator (Dan) does not need to ask permission — certain triggers require certain agents.</p>

<table class="trigger-table">
  <thead>
    <tr><th>Trigger / Situation</th><th>Agent to Launch</th></tr>
  </thead>
  <tbody>
    <tr><td>After any code change</td><td><strong>Asha</strong> (QA testing)</td></tr>
    <tr><td>Before any git commit</td><td><strong>Bill</strong> (code review, must get +2)</td></tr>
    <tr><td>UI/UX decisions or new screens / design system</td><td><strong>Maya</strong> (UI/UX Architect)</td></tr>
    <tr><td>Documentation, user guides, release notes</td><td><strong>Elena</strong> (Technical Writer)</td></tr>
    <tr><td>Marketing strategy, launches, campaigns</td><td><strong>Nina</strong> (Head of Marketing)</td></tr>
    <tr><td>Marketing copy / user messaging / UI microcopy</td><td><strong>Jamie</strong> (Copy Writer, under Nina)</td></tr>
    <tr><td>Requirements intake / customer interviews</td><td><strong>Kim</strong> (Product Manager)</td></tr>
    <tr><td>Database schema changes</td><td><strong>Raj</strong> (DBA)</td></tr>
    <tr><td>Hockey-specific features</td><td><strong>Bedsy</strong> (Hockey Consultant)</td></tr>
    <tr><td>Analytics/tracking work</td><td><strong>Mei</strong> (Data Analyst)</td></tr>
    <tr><td>App Store / mobile deployment</td><td><strong>Carlos</strong> (Mobile Developer)</td></tr>
    <tr><td>Sprint planning / status reports</td><td><strong>Sam</strong> (Scrum Master)</td></tr>
    <tr><td>Privacy / compliance / user data</td><td><strong>Sarah</strong> (Compliance &amp; Legal)</td></tr>
    <tr><td>HIPAA / SOC / PHI / PII handling</td><td><strong>Gregory</strong> (HIPAA &amp; SOC Auditor)</td></tr>
    <tr><td>Network / Azure / Intune</td><td><strong>Todd</strong> (Network Admin)</td></tr>
  </tbody>
</table>

<h3>Collaboration Patterns</h3>

<div class="expandable" data-title="Feature Implementation Flow">
  <ol>
    <li>Maya designs the UI/UX</li>
    <li>Marcus implements the code</li>
    <li>Asha tests the full user flow</li>
    <li>Marcus fixes any bugs found</li>
    <li>Bill reviews the code (must get +2)</li>
    <li>Commit with review trailers</li>
  </ol>
</div>

<div class="expandable" data-title="Bug Fix Flow">
  <ol>
    <li>Asha reports the bug with reproduction steps</li>
    <li>Marcus fixes the issue</li>
    <li>Asha verifies the fix</li>
    <li>Bill reviews the code change</li>
    <li>Commit with review trailers</li>
  </ol>
</div>

<div class="expandable" data-title="Database Change Flow">
  <ol>
    <li>Raj designs the schema changes</li>
    <li>Marcus implements the migration</li>
    <li>Asha tests data integrity</li>
    <li>Raj reviews the implementation</li>
    <li>Bill does final code review</li>
    <li>Commit with review trailers</li>
  </ol>
</div>
`,
      },
      {
        id: 'persona-system',
        title: 'The Persona System',
        content: `
<h2>The Persona System</h2>
<p>The AIDLC Admin Portal uses a <strong>persona system</strong> to control what each user sees. This is not authentication — it is a UI lens that filters the navigation and dashboard to show only what is relevant to each role.</p>

<h3>Available Personas</h3>

<table>
  <thead><tr><th>Persona</th><th>Label</th><th>Who Uses It</th><th>What They See</th></tr></thead>
  <tbody>
    <tr><td><strong>Executive</strong></td><td>CEO / Customer</td><td>The CEO</td><td>Budget, roadmap, milestones, high-level monitoring</td></tr>
    <tr><td><strong>Ops</strong></td><td>Operations Director</td><td>Dan</td><td>Everything (full admin access)</td></tr>
    <tr><td><strong>Orchestrator</strong></td><td>Scrum Master</td><td>Sam</td><td>Planning, sprints, agent scheduling, assessments</td></tr>
    <tr><td><strong>Builder</strong></td><td>Developer</td><td>Marcus, etc.</td><td>Sprint board, backlog, assigned work</td></tr>
    <tr><td><strong>Gatekeeper</strong></td><td>Reviewer</td><td>Bill, Asha</td><td>DoD pipeline, escalation queue, review reports</td></tr>
  </tbody>
</table>

<div class="callout tip">
  <strong>Tip:</strong> The persona switcher is in the sidebar, below the LDAJA logo. Switching personas immediately changes which navigation items are visible.
</div>

<h3>Why Personas Matter</h3>
<p>Agents have personalities for a reason. A code reviewer who is lenient produces buggy software. A QA tester who rubber-stamps produces untested software. Each persona's personality traits are calibrated to produce the best output for their role:</p>

<ul>
  <li><strong>Bill</strong> is strict and exacting because code quality cannot be compromised.</li>
  <li><strong>Asha</strong> is thorough and skeptical because "if it's not tested, it's broken."</li>
  <li><strong>Marcus</strong> is action-oriented because features need to ship.</li>
  <li><strong>Sam</strong> is organized and process-focused because sprints need structure.</li>
  <li><strong>Sarah</strong> is cautious because compliance violations have real consequences.</li>
</ul>
`,
      },
      {
        id: 'agent-communication',
        title: 'Agent Communication',
        content: `
<h2>Agent Communication via the Bridge</h2>
<p>Agents communicate with the admin portal through two mechanisms:</p>

<h3>1. Bridge Status Reporting</h3>
<p>Every agent <strong>must</strong> report their status when starting and finishing work. This is done via the bridge report script:</p>

<div class="code-block">
<pre># When starting work:
node bridge/report.js --agent marcus --status working \\
  --task "Implementing training platform"

# When finishing work:
node bridge/report.js --agent marcus --status available \\
  --tokens 5000</pre>
</div>

<h3>2. Audit Entry Logging</h3>
<p>All significant agent actions are logged to the audit trail:</p>

<div class="code-block">
<pre># Log agent spawn:
node bridge/report-audit.js --agent marcus \\
  --action agent_spawned --task "Training platform"

# Log agent completion:
node bridge/report-audit.js --agent marcus \\
  --action agent_completed --task "Training platform" \\
  --tokens 3500</pre>
</div>

<h3>3. AgentHub Posts</h3>
<p>The AgentHub (<code>docs/AGENT_COMMUNICATIONS_DASHBOARD.html</code>) is the team's social communication platform. Agents post structured summaries when completing work:</p>

<div class="code-block">
<pre>## AgentHub Post
**Agent:** Marcus Chen
**Project:** AIDLC Admin Portal
**Type:** task-complete
**Subject:** Training Platform Implementation
**Content:** Built interactive training platform with 3 modules...
**Tags:** training, frontend, react</pre>
</div>

<div class="callout warning">
  <strong>Critical Rule:</strong> The CEO reviews AgentHub to track all agent outputs. Work that is not logged may be lost in CLI scrollback. If you did the work, log it.
</div>
`,
      },
    ],
    quiz: [
      {
        question: 'Which agent MUST review code before any git commit?',
        options: [
          'Asha Patel (QA)',
          'Sam Kennedy (Scrum)',
          'Bill Torres (Code Review)',
          'Marcus Chen (Dev)',
        ],
        correctIndex: 2,
        explanation: 'Bill "The Gatekeeper" Torres must review and approve (+2) every code change before it can be committed. This is non-negotiable.',
      },
      {
        question: 'What does the "Ops" persona see in the admin portal?',
        options: [
          'Only the sprint board and backlog',
          'Everything — full admin access',
          'Only budget and monitoring',
          'Only the DoD pipeline and reviews',
        ],
        correctIndex: 1,
        explanation: 'The Ops persona (Dan, Operations Director) always has full visibility across every section and feature in the admin portal.',
      },
      {
        question: 'How do agents report their status to the admin portal?',
        options: [
          'By writing directly to the database',
          'By calling a REST API endpoint',
          'By running the bridge/report.js script which writes JSON files',
          'By posting to a Slack channel',
        ],
        correctIndex: 2,
        explanation: 'Agents report status via bridge/report.js, which writes to bridge/agent-status.json. The backend watches this file and pushes updates via WebSocket.',
      },
    ],
  },

  // ── MODULE 3: The Bridge System ──
  {
    id: 'bridge',
    title: 'The Bridge System',
    description: 'Deep dive into the file-based JSON IPC bridge that connects CLI agents to the web-based admin portal in real time.',
    icon: '\u{1F309}',
    difficulty: 'Intermediate',
    estimatedMinutes: 25,
    sections: [
      {
        id: 'what-is-bridge',
        title: 'What is the Bridge?',
        content: `
<h2>What is the Bridge?</h2>
<p>The <strong>Bridge</strong> is a file-based inter-process communication (IPC) layer that connects CLI-based Claude Code agent sessions to the web-based AIDLC Admin Portal.</p>

<h3>The Problem It Solves</h3>
<p>Claude Code agents run in terminal sessions — they have no direct connection to the browser-based admin dashboard. The bridge solves this by using <strong>JSON files on disk</strong> as a shared communication channel:</p>

<div class="diagram">
  <div class="diagram-row">
    <div class="diagram-box green">CLI Agent<br/><span class="small">Terminal session</span></div>
    <div class="diagram-arrow">\u2192 writes \u2192</div>
    <div class="diagram-box orange">JSON File<br/><span class="small">On disk</span></div>
    <div class="diagram-arrow">\u2192 reads \u2192</div>
    <div class="diagram-box indigo">Backend<br/><span class="small">Express server</span></div>
  </div>
</div>

<h3>Why Files Instead of HTTP?</h3>
<ul>
  <li><strong>Simplicity:</strong> No need for agents to make HTTP requests or manage auth tokens.</li>
  <li><strong>Reliability:</strong> File writes are atomic and work even if the server is restarting.</li>
  <li><strong>Decoupling:</strong> Agents do not need to know the server's URL or port.</li>
  <li><strong>Auditability:</strong> Every bridge file change is visible in git status.</li>
</ul>

<div class="callout info">
  <strong>Bridge Location:</strong> All bridge files live in <code>bridge/</code> in the <code>aidlc-portal</code> repository.
</div>
`,
      },
      {
        id: 'agent-status-json',
        title: 'How agent-status.json Works',
        content: `
<h2>How agent-status.json Works</h2>
<p>The primary bridge file is <code>bridge/agent-status.json</code>. When an agent runs <code>bridge/report.js</code>, it writes the agent's current status into this file.</p>

<h3>File Structure</h3>
<div class="code-block">
<pre>{
  "marcus": {
    "agentId": "marcus",
    "status": "working",
    "task": "Building training platform",
    "timestamp": "2025-02-10T14:30:00.000Z",
    "tokens": 0
  },
  "bill": {
    "agentId": "bill",
    "status": "available",
    "task": "",
    "timestamp": "2025-02-10T14:25:00.000Z",
    "tokens": 3500
  }
}</pre>
</div>

<h3>Valid Status Values</h3>
<table>
  <thead><tr><th>Status</th><th>Meaning</th><th>Dashboard Display</th></tr></thead>
  <tbody>
    <tr><td><code>working</code></td><td>Agent is actively executing a task</td><td>Green pulsing border</td></tr>
    <tr><td><code>available</code></td><td>Agent is idle, ready for work</td><td>Gray card, no pulse</td></tr>
    <tr><td><code>waiting</code></td><td>Agent is blocked on another agent</td><td>Yellow indicator</td></tr>
    <tr><td><code>away</code></td><td>Agent is not active</td><td>Dimmed card</td></tr>
  </tbody>
</table>

<h3>What Happens After a Write</h3>
<ol>
  <li>Agent runs <code>node bridge/report.js --agent marcus --status working --task "..."</code></li>
  <li>The script reads <code>agent-status.json</code>, updates the agent's entry, writes it back.</li>
  <li>The backend's file watcher detects the change (polling every 1 second).</li>
  <li>The in-memory store updates the agent's status.</li>
  <li>Socket.io pushes the update to all connected browser clients.</li>
  <li>The Agent Dashboard card shows the status change in real time.</li>
</ol>
`,
      },
      {
        id: 'chat-flow',
        title: 'Chat Message Flow',
        content: `
<h2>Chat Message Flow</h2>
<p>The admin portal includes a chat interface that lets the CEO send messages to agents. This also flows through the bridge:</p>

<div class="flow-diagram">
  <div class="flow-step">
    <div class="flow-num">1</div>
    <div class="flow-box blue">CEO types message<br/><span class="small">Browser chat UI</span></div>
  </div>
  <div class="flow-connector">\u2193</div>
  <div class="flow-step">
    <div class="flow-num">2</div>
    <div class="flow-box indigo">Frontend sends to backend<br/><span class="small">POST /api/chat or WebSocket</span></div>
  </div>
  <div class="flow-connector">\u2193</div>
  <div class="flow-step">
    <div class="flow-num">3</div>
    <div class="flow-box orange">Backend writes to bridge<br/><span class="small">bridge/chat-outgoing.json</span></div>
  </div>
  <div class="flow-connector">\u2193</div>
  <div class="flow-step">
    <div class="flow-num">4</div>
    <div class="flow-box green">Agent dispatcher reads file<br/><span class="small">CLI polling process</span></div>
  </div>
  <div class="flow-connector">\u2193</div>
  <div class="flow-step">
    <div class="flow-num">5</div>
    <div class="flow-box green">Agent processes &amp; responds<br/><span class="small">Writes bridge/chat-incoming.json</span></div>
  </div>
  <div class="flow-connector">\u2193</div>
  <div class="flow-step">
    <div class="flow-num">6</div>
    <div class="flow-box indigo">Backend detects response<br/><span class="small">File watcher picks up change</span></div>
  </div>
  <div class="flow-connector">\u2193</div>
  <div class="flow-step">
    <div class="flow-num">7</div>
    <div class="flow-box blue">Response pushed to browser<br/><span class="small">WebSocket \u2192 Chat UI</span></div>
  </div>
</div>
`,
      },
      {
        id: 'audit-entries',
        title: 'Audit Entry Logging',
        content: `
<h2>Audit Entry Logging</h2>
<p>Every significant agent action is recorded in the audit trail via <code>bridge/report-audit.js</code>. This creates an immutable log of what happened, when, and by whom.</p>

<h3>Audit Entry Structure</h3>
<div class="code-block">
<pre>{
  "id": "AUD-a1b2c3d4",
  "timestamp": "2025-02-10T14:30:00.000Z",
  "agentId": "bill",
  "action": "code_reviewed",
  "task": "Training platform implementation",
  "tokens": 2500,
  "metadata": {}
}</pre>
</div>

<h3>Valid Audit Actions</h3>
<div class="tag-grid">
  <span class="tag">agent_spawned</span>
  <span class="tag">agent_completed</span>
  <span class="tag">agent_failed</span>
  <span class="tag">task_assigned</span>
  <span class="tag">task_completed</span>
  <span class="tag">code_reviewed</span>
  <span class="tag">status_changed</span>
  <span class="tag">prompt_queued</span>
  <span class="tag">prompt_completed</span>
  <span class="tag">governance_triggered</span>
</div>

<h3>The Orchestrator Pattern</h3>
<p>When Dan (the orchestrator) spawns an agent, he must report both <strong>before</strong> and <strong>after</strong>:</p>

<div class="code-block">
<pre># BEFORE spawning Bill:
node bridge/report.js --agent bill --status working \\
  --task "Code review for feature X"
node bridge/report-audit.js --agent bill \\
  --action agent_spawned --task "Code review for feature X"

# AFTER Bill completes:
node bridge/report.js --agent bill --status available \\
  --tokens 3500
node bridge/report-audit.js --agent bill \\
  --action agent_completed \\
  --task "Code review for feature X" --tokens 3500</pre>
</div>
`,
      },
      {
        id: 'polling-cycle',
        title: 'The 1-Second Polling Cycle',
        content: `
<h2>The 1-Second Polling Cycle</h2>
<p>The backend server uses <code>fs.watchFile</code> with a 1-second polling interval to detect bridge file changes. This creates a near-real-time communication channel without the complexity of file system event watchers (which can be unreliable on Windows).</p>

<h3>Polling Architecture</h3>

<div class="diagram">
  <div class="diagram-row">
    <div class="diagram-box orange">fs.watchFile<br/><span class="small">1000ms interval</span></div>
    <div class="diagram-arrow">\u2192</div>
    <div class="diagram-box green">File changed?<br/><span class="small">Compare mtime</span></div>
    <div class="diagram-arrow">\u2192</div>
    <div class="diagram-box indigo">Parse JSON<br/><span class="small">Update store</span></div>
  </div>
  <div class="diagram-row">
    <div class="diagram-box indigo">Store updated<br/><span class="small">In-memory</span></div>
    <div class="diagram-arrow">\u2192</div>
    <div class="diagram-box purple">Socket.io emit<br/><span class="small">Broadcast to clients</span></div>
    <div class="diagram-arrow">\u2192</div>
    <div class="diagram-box blue">Dashboard refresh<br/><span class="small">React re-render</span></div>
  </div>
</div>

<h3>Files Being Watched</h3>
<table>
  <thead><tr><th>File</th><th>Purpose</th></tr></thead>
  <tbody>
    <tr><td><code>bridge/agent-status.json</code></td><td>Agent working/available/waiting status</td></tr>
    <tr><td><code>bridge/chat-outgoing.json</code></td><td>Messages from CEO to agents</td></tr>
    <tr><td><code>bridge/chat-incoming.json</code></td><td>Responses from agents to CEO</td></tr>
    <tr><td><code>bridge/audit-entry.json</code></td><td>New audit log entries</td></tr>
  </tbody>
</table>

<div class="callout tip">
  <strong>Performance Note:</strong> The 1-second poll interval means there is up to a 1-second delay between an agent writing a bridge file and the dashboard reflecting the change. This is acceptable for the current use case.
</div>

<h3>What Triggers a Dashboard Update</h3>
<ol>
  <li>Agent writes to a bridge file (e.g., <code>agent-status.json</code>)</li>
  <li>Backend's <code>fs.watchFile</code> detects mtime change within 1 second</li>
  <li>Backend reads and parses the updated JSON</li>
  <li>In-memory store is updated</li>
  <li>Socket.io broadcasts <code>agent:status-update</code> event to all connected clients</li>
  <li>React components subscribed to the bridge context re-render</li>
  <li>Dashboard shows updated agent card with green pulse animation (10 seconds)</li>
  <li>The LIVE badge in the header pulses to indicate recent activity</li>
</ol>
`,
      },
    ],
    quiz: [
      {
        question: 'Why does the bridge use JSON files on disk instead of HTTP API calls?',
        options: [
          'JSON files are faster than HTTP',
          'The server does not support HTTP',
          'Simplicity, reliability, decoupling — agents just write files without needing server URLs or auth',
          'JSON files support larger payloads',
        ],
        correctIndex: 2,
        explanation: 'File-based IPC is simpler (no HTTP client needed), reliable (works even during server restarts), and decoupled (agents do not need to know the server address).',
      },
      {
        question: 'How frequently does the backend poll bridge files for changes?',
        options: [
          'Every 100 milliseconds',
          'Every 500 milliseconds',
          'Every 1 second',
          'Every 5 seconds',
        ],
        correctIndex: 2,
        explanation: 'The backend uses fs.watchFile with a 1-second (1000ms) polling interval to detect bridge file changes.',
      },
      {
        question: 'What is the correct sequence when the CEO sends a chat message to an agent?',
        options: [
          'Browser -> WebSocket -> Agent directly',
          'Browser -> Backend -> chat-outgoing.json -> Agent dispatcher -> Agent -> chat-incoming.json -> Backend -> Browser',
          'Browser -> Firebase -> Agent -> Firebase -> Browser',
          'Browser -> REST API -> Agent process -> REST API -> Browser',
        ],
        correctIndex: 1,
        explanation: 'Chat messages flow: Browser -> Backend -> bridge/chat-outgoing.json -> agent dispatcher reads file -> agent responds to chat-incoming.json -> backend detects -> WebSocket -> Browser.',
      },
    ],
  },
];

// ─── Sub-components ───────────────────────────────────────────────

/** Difficulty badge */
function DifficultyBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    Beginner: 'bg-green-500/20 text-green-400 border-green-500/30',
    Intermediate: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    Advanced: 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${colors[level] || colors.Beginner}`}>
      {level}
    </span>
  );
}

/** Progress bar */
function ProgressBar({ pct, className = '' }: { pct: number; className?: string }) {
  return (
    <div className={`w-full h-2 rounded-full bg-white/10 overflow-hidden ${className}`}>
      <div
        className="h-full rounded-full bg-indigo-500 transition-all duration-500"
        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
      />
    </div>
  );
}

/** Checkmark icon */
function Check({ done }: { done: boolean }) {
  if (!done) return <span className="w-5 h-5 rounded-full border-2 border-white/20 inline-block flex-shrink-0" />;
  return (
    <span className="w-5 h-5 rounded-full bg-green-500 inline-flex items-center justify-center flex-shrink-0">
      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
        <path d="M5 13l4 4L19 7" />
      </svg>
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────

export function TrainingPage() {
  const [progress, setProgress] = useState<TrainingProgress>(loadProgress);
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<number>(0);
  const [quizMode, setQuizMode] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<Record<number, number>>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Persist progress
  useEffect(() => { saveProgress(progress); }, [progress]);

  // Compute module progress
  const getModuleProgress = useCallback((mod: TrainingModule): number => {
    const completed = progress.completedSections[mod.id] || [];
    const total = mod.sections.length + 1; // sections + quiz
    const quizDone = (progress.quizScores[mod.id] ?? -1) >= 0 ? 1 : 0;
    return Math.round(((completed.length + quizDone) / total) * 100);
  }, [progress]);

  // Overall progress
  const overallProgress = useMemo(() => {
    const totalItems = MODULES.reduce((sum, m) => sum + m.sections.length + 1, 0);
    const completedItems = MODULES.reduce((sum, m) => {
      const sections = (progress.completedSections[m.id] || []).length;
      const quiz = (progress.quizScores[m.id] ?? -1) >= 0 ? 1 : 0;
      return sum + sections + quiz;
    }, 0);
    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  }, [progress]);

  // Mark section complete
  const markSectionComplete = useCallback((moduleId: string, sectionId: string) => {
    setProgress((prev) => {
      const existing = prev.completedSections[moduleId] || [];
      if (existing.includes(sectionId)) return prev;
      return {
        ...prev,
        completedSections: {
          ...prev.completedSections,
          [moduleId]: [...existing, sectionId],
        },
      };
    });
  }, []);

  // Navigate sections
  const currentModule = MODULES.find((m) => m.id === activeModule);

  const goToSection = useCallback((idx: number) => {
    if (!currentModule) return;
    // Mark current section complete when navigating away
    if (activeSection < currentModule.sections.length) {
      markSectionComplete(currentModule.id, currentModule.sections[activeSection].id);
    }
    if (idx >= currentModule.sections.length) {
      setQuizMode(true);
      setActiveSection(currentModule.sections.length - 1);
    } else {
      setQuizMode(false);
      setActiveSection(idx);
    }
  }, [currentModule, activeSection, markSectionComplete]);

  const goNext = useCallback(() => {
    if (!currentModule) return;
    if (quizMode) return;
    if (activeSection < currentModule.sections.length - 1) {
      goToSection(activeSection + 1);
    } else {
      goToSection(currentModule.sections.length); // go to quiz
    }
  }, [currentModule, activeSection, quizMode, goToSection]);

  const goPrev = useCallback(() => {
    if (!currentModule) return;
    if (quizMode) {
      setQuizMode(false);
      setActiveSection(currentModule.sections.length - 1);
    } else if (activeSection > 0) {
      goToSection(activeSection - 1);
    }
  }, [currentModule, activeSection, quizMode, goToSection]);

  const goBackToDashboard = useCallback(() => {
    // Mark last section complete when leaving
    if (currentModule && activeSection < currentModule.sections.length) {
      markSectionComplete(currentModule.id, currentModule.sections[activeSection].id);
    }
    setActiveModule(null);
    setActiveSection(0);
    setQuizMode(false);
    setQuizAnswers({});
    setQuizSubmitted(false);
  }, [currentModule, activeSection, markSectionComplete]);

  // Quiz handlers
  const handleQuizAnswer = useCallback((qIdx: number, optIdx: number) => {
    if (quizSubmitted) return;
    setQuizAnswers((prev) => ({ ...prev, [qIdx]: optIdx }));
  }, [quizSubmitted]);

  const handleQuizSubmit = useCallback(() => {
    if (!currentModule) return;
    const score = currentModule.quiz.reduce((sum, q, i) => {
      return sum + (quizAnswers[i] === q.correctIndex ? 1 : 0);
    }, 0);
    setQuizSubmitted(true);
    setProgress((prev) => ({
      ...prev,
      quizScores: {
        ...prev.quizScores,
        [currentModule.id]: Math.max(prev.quizScores[currentModule.id] ?? -1, score),
      },
    }));
  }, [currentModule, quizAnswers]);

  // Toggle expandable
  const toggleExpanded = useCallback((key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Reset progress
  const resetProgress = useCallback(() => {
    const empty: TrainingProgress = { completedSections: {}, quizScores: {} };
    setProgress(empty);
    saveProgress(empty);
  }, []);

  // ─── Render Content HTML with interactive elements ──────────────
  const renderContent = useCallback((html: string) => {
    // Process expandable sections and inject interactivity
    const parts = html.split(/(<div class="expandable"[^>]*>[\s\S]*?<\/div>)/g);

    return parts.map((part, idx) => {
      const expandMatch = part.match(/<div class="expandable" data-title="([^"]+)">([\s\S]*?)<\/div>/);
      if (expandMatch) {
        const title = expandMatch[1];
        const inner = expandMatch[2];
        const key = `${activeModule}-${activeSection}-${idx}`;
        const isOpen = expandedSections.has(key);
        return (
          <div key={key} className="my-3 border border-white/10 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleExpanded(key)}
              className="w-full flex items-center justify-between px-4 py-3 bg-white/5 hover:bg-white/10 transition-colors text-left"
            >
              <span className="text-sm font-medium text-white">{title}</span>
              <svg
                className={`w-4 h-4 text-white/40 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </button>
            {isOpen && (
              <div
                className="px-4 py-3 text-sm text-gray-300 training-content"
                dangerouslySetInnerHTML={{ __html: inner }}
              />
            )}
          </div>
        );
      }
      return (
        <div
          key={idx}
          className="training-content"
          dangerouslySetInnerHTML={{ __html: part }}
        />
      );
    });
  }, [activeModule, activeSection, expandedSections, toggleExpanded]);

  // ─── DASHBOARD VIEW ──────────────────────────────────────────────

  if (!activeModule) {
    return (
      <div className="-m-6 min-h-screen bg-[#0f1117] text-white">
        <style>{trainingStyles}</style>
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Training Platform</h1>
            <p className="text-gray-400">AIDLC onboarding and system documentation. Complete all modules to get up to speed.</p>
          </div>

          {/* Overall Progress Banner */}
          <div className="bg-[#1a1d27] border border-[#2a2d3a] rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold">Overall Progress</h2>
                <p className="text-sm text-gray-400 mt-1">
                  {overallProgress === 100
                    ? 'Congratulations! All modules completed.'
                    : `${overallProgress}% complete \u2014 keep going!`}
                </p>
              </div>
              <div className="text-right">
                <span className="text-3xl font-bold text-indigo-400">{overallProgress}%</span>
              </div>
            </div>
            <ProgressBar pct={overallProgress} />
            {overallProgress > 0 && (
              <button
                onClick={resetProgress}
                className="mt-3 text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                Reset all progress
              </button>
            )}
          </div>

          {/* Module Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {MODULES.map((mod) => {
              const pct = getModuleProgress(mod);
              const quizScore = progress.quizScores[mod.id];
              return (
                <button
                  key={mod.id}
                  onClick={() => {
                    setActiveModule(mod.id);
                    setActiveSection(0);
                    setQuizMode(false);
                    setQuizAnswers({});
                    setQuizSubmitted(false);
                  }}
                  className="bg-[#1a1d27] border border-[#2a2d3a] rounded-xl p-6 text-left hover:border-indigo-500/50 hover:bg-[#1e2130] transition-all group"
                >
                  {/* Icon + Difficulty */}
                  <div className="flex items-start justify-between mb-4">
                    <span className="text-4xl">{mod.icon}</span>
                    <DifficultyBadge level={mod.difficulty} />
                  </div>

                  {/* Title + Description */}
                  <h3 className="text-lg font-semibold mb-2 group-hover:text-indigo-400 transition-colors">
                    {mod.title}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4 line-clamp-2">{mod.description}</p>

                  {/* Meta */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                    <span>{mod.sections.length} sections</span>
                    <span>{mod.estimatedMinutes} min</span>
                    {quizScore !== undefined && quizScore >= 0 && (
                      <span className="text-green-400">Quiz: {quizScore}/{mod.quiz.length}</span>
                    )}
                  </div>

                  {/* Progress */}
                  <ProgressBar pct={pct} />
                  <div className="text-xs text-gray-500 mt-1.5">{pct}% complete</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ─── LESSON VIEW ─────────────────────────────────────────────────

  if (!currentModule) return null;

  const section = currentModule.sections[activeSection];
  const completedSections = progress.completedSections[currentModule.id] || [];

  return (
    <div className="-m-6 min-h-screen bg-[#0f1117] text-white">
      <style>{trainingStyles}</style>
      <div className="flex min-h-screen">
        {/* Left Sidebar - Module Outline */}
        <div className="w-64 flex-shrink-0 bg-[#13161f] border-r border-[#2a2d3a] p-4 sticky top-0 h-screen overflow-y-auto">
          <button
            onClick={goBackToDashboard}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-6"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            Back to Dashboard
          </button>

          <h2 className="text-sm font-semibold text-white mb-1">{currentModule.title}</h2>
          <p className="text-xs text-gray-500 mb-4">{currentModule.estimatedMinutes} min</p>

          <ul className="space-y-1">
            {currentModule.sections.map((s, idx) => {
              const isDone = completedSections.includes(s.id);
              const isActive = !quizMode && idx === activeSection;
              return (
                <li key={s.id}>
                  <button
                    onClick={() => goToSection(idx)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                      isActive
                        ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                        : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
                    }`}
                  >
                    <Check done={isDone} />
                    <span className="truncate">{s.title}</span>
                  </button>
                </li>
              );
            })}
            {/* Quiz entry */}
            <li className="pt-2 mt-2 border-t border-white/10">
              <button
                onClick={() => goToSection(currentModule.sections.length)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                  quizMode
                    ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                    : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
                }`}
              >
                <Check done={(progress.quizScores[currentModule.id] ?? -1) >= 0} />
                <span>Quiz ({currentModule.quiz.length} questions)</span>
              </button>
            </li>
          </ul>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 max-w-4xl px-8 py-8">
          {!quizMode ? (
            <>
              {/* Section Content */}
              <div className="mb-8">
                {renderContent(section.content)}
              </div>

              {/* Bottom Navigation */}
              <div className="flex items-center justify-between pt-6 border-t border-[#2a2d3a]">
                <button
                  onClick={goPrev}
                  disabled={activeSection === 0}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                  Previous
                </button>

                <span className="text-xs text-gray-500">
                  Section {activeSection + 1} of {currentModule.sections.length}
                </span>

                <button
                  onClick={goNext}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-500 transition-colors"
                >
                  {activeSection === currentModule.sections.length - 1 ? 'Go to Quiz' : 'Next'}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </button>
              </div>
            </>
          ) : (
            /* Quiz View */
            <div>
              <h2 className="text-2xl font-bold mb-2">Quiz: {currentModule.title}</h2>
              <p className="text-sm text-gray-400 mb-8">
                Answer all {currentModule.quiz.length} questions. You need to answer to complete the module.
              </p>

              <div className="space-y-8">
                {currentModule.quiz.map((q, qIdx) => {
                  const selected = quizAnswers[qIdx];
                  const isCorrect = selected === q.correctIndex;
                  return (
                    <div key={qIdx} className="bg-[#1a1d27] border border-[#2a2d3a] rounded-xl p-6">
                      <h3 className="text-base font-semibold mb-4">
                        <span className="text-indigo-400 mr-2">Q{qIdx + 1}.</span>
                        {q.question}
                      </h3>
                      <div className="space-y-2">
                        {q.options.map((opt, oIdx) => {
                          let optClass = 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20';
                          if (quizSubmitted) {
                            if (oIdx === q.correctIndex) {
                              optClass = 'bg-green-500/20 border-green-500/50 text-green-300';
                            } else if (oIdx === selected && !isCorrect) {
                              optClass = 'bg-red-500/20 border-red-500/50 text-red-300';
                            } else {
                              optClass = 'bg-white/5 border-white/10 opacity-50';
                            }
                          } else if (oIdx === selected) {
                            optClass = 'bg-indigo-500/20 border-indigo-500/50 text-indigo-300';
                          }
                          return (
                            <button
                              key={oIdx}
                              onClick={() => handleQuizAnswer(qIdx, oIdx)}
                              disabled={quizSubmitted}
                              className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-all ${optClass}`}
                            >
                              <span className="font-medium mr-2">{String.fromCharCode(65 + oIdx)}.</span>
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                      {quizSubmitted && (
                        <div className={`mt-4 p-3 rounded-lg text-sm ${
                          isCorrect ? 'bg-green-500/10 text-green-300' : 'bg-red-500/10 text-red-300'
                        }`}>
                          <strong>{isCorrect ? 'Correct!' : 'Incorrect.'}</strong> {q.explanation}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Submit / Results */}
              <div className="mt-8 flex items-center justify-between">
                <button
                  onClick={goPrev}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                  Previous Section
                </button>

                {!quizSubmitted ? (
                  <button
                    onClick={handleQuizSubmit}
                    disabled={Object.keys(quizAnswers).length < currentModule.quiz.length}
                    className="px-6 py-2 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Submit Answers
                  </button>
                ) : (
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-400">
                      Score: {currentModule.quiz.reduce((s, q, i) => s + (quizAnswers[i] === q.correctIndex ? 1 : 0), 0)}/{currentModule.quiz.length}
                    </span>
                    <button
                      onClick={goBackToDashboard}
                      className="px-6 py-2 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-500 transition-colors"
                    >
                      Back to Dashboard
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Embedded Styles for Training Content ─────────────────────────

const trainingStyles = `
  /* ── Base Content Styling ─────────────────────────── */
  .training-content h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #fff;
    margin-bottom: 0.75rem;
    margin-top: 0;
  }
  .training-content h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #e2e8f0;
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
  }
  .training-content p {
    color: #94a3b8;
    line-height: 1.75;
    margin-bottom: 0.75rem;
    font-size: 0.9375rem;
  }
  .training-content ul, .training-content ol {
    color: #94a3b8;
    margin-left: 1.25rem;
    margin-bottom: 1rem;
    line-height: 1.75;
    font-size: 0.9375rem;
  }
  .training-content li {
    margin-bottom: 0.375rem;
  }
  .training-content strong {
    color: #e2e8f0;
  }
  .training-content em {
    color: #a5b4fc;
  }
  .training-content code {
    background: rgba(99, 102, 241, 0.15);
    color: #a5b4fc;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    font-size: 0.85em;
    font-family: 'Fira Code', 'Cascadia Code', 'JetBrains Mono', monospace;
  }

  /* ── Tables ──────────────────────────────────────── */
  .training-content table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
    font-size: 0.875rem;
  }
  .training-content th {
    background: rgba(99, 102, 241, 0.15);
    color: #a5b4fc;
    text-align: left;
    padding: 0.625rem 0.75rem;
    font-weight: 600;
    border-bottom: 1px solid #2a2d3a;
  }
  .training-content td {
    padding: 0.5rem 0.75rem;
    color: #94a3b8;
    border-bottom: 1px solid #1e2130;
  }
  .training-content tr:hover td {
    background: rgba(255, 255, 255, 0.02);
  }
  .training-content .agent-table td:first-child,
  .training-content .trigger-table td:last-child {
    color: #e2e8f0;
    font-weight: 500;
  }

  /* ── Code Blocks ─────────────────────────────────── */
  .training-content .code-block {
    background: #0d1017;
    border: 1px solid #2a2d3a;
    border-radius: 0.5rem;
    margin: 1rem 0;
    overflow-x: auto;
  }
  .training-content .code-block pre {
    padding: 1rem;
    margin: 0;
    color: #a5b4fc;
    font-family: 'Fira Code', 'Cascadia Code', 'JetBrains Mono', monospace;
    font-size: 0.8125rem;
    line-height: 1.6;
    white-space: pre;
  }

  /* ── Callout Boxes ───────────────────────────────── */
  .training-content .callout {
    padding: 0.875rem 1rem;
    border-radius: 0.5rem;
    margin: 1rem 0;
    font-size: 0.875rem;
    line-height: 1.6;
    border-left: 3px solid;
  }
  .training-content .callout.info {
    background: rgba(59, 130, 246, 0.1);
    border-color: #3b82f6;
    color: #93c5fd;
  }
  .training-content .callout.warning {
    background: rgba(245, 158, 11, 0.1);
    border-color: #f59e0b;
    color: #fcd34d;
  }
  .training-content .callout.tip {
    background: rgba(34, 197, 94, 0.1);
    border-color: #22c55e;
    color: #86efac;
  }
  .training-content .callout strong {
    color: inherit;
  }

  /* ── Architecture Diagrams ───────────────────────── */
  .training-content .diagram {
    background: #0d1017;
    border: 1px solid #2a2d3a;
    border-radius: 0.75rem;
    padding: 1.5rem;
    margin: 1.5rem 0;
  }
  .training-content .diagram-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }
  .training-content .diagram-row:last-child {
    margin-bottom: 0;
  }
  .training-content .diagram-box {
    padding: 0.625rem 1rem;
    border-radius: 0.5rem;
    text-align: center;
    font-size: 0.8125rem;
    font-weight: 600;
    min-width: 120px;
    border: 1px solid;
  }
  .training-content .diagram-box .small {
    display: block;
    font-size: 0.6875rem;
    font-weight: 400;
    opacity: 0.7;
    margin-top: 0.125rem;
  }
  .training-content .diagram-box.blue {
    background: rgba(59, 130, 246, 0.15);
    border-color: rgba(59, 130, 246, 0.3);
    color: #93c5fd;
  }
  .training-content .diagram-box.green {
    background: rgba(34, 197, 94, 0.15);
    border-color: rgba(34, 197, 94, 0.3);
    color: #86efac;
  }
  .training-content .diagram-box.orange {
    background: rgba(245, 158, 11, 0.15);
    border-color: rgba(245, 158, 11, 0.3);
    color: #fcd34d;
  }
  .training-content .diagram-box.purple {
    background: rgba(168, 85, 247, 0.15);
    border-color: rgba(168, 85, 247, 0.3);
    color: #d8b4fe;
  }
  .training-content .diagram-box.indigo {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.3);
    color: #a5b4fc;
  }
  .training-content .diagram-arrow {
    color: #4b5563;
    font-size: 1.25rem;
    flex-shrink: 0;
  }

  /* ── Architecture Layer Diagram ──────────────────── */
  .training-content .arch-diagram {
    margin: 1.5rem 0;
  }
  .training-content .arch-layer {
    border: 1px solid;
    border-radius: 0.75rem;
    overflow: hidden;
    margin-bottom: 0;
  }
  .training-content .arch-label {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .training-content .arch-items {
    display: flex;
    gap: 0.75rem;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.3);
    flex-wrap: wrap;
  }
  .training-content .arch-item {
    flex: 1;
    min-width: 140px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.5rem;
    padding: 0.75rem;
    text-align: center;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #e2e8f0;
  }
  .training-content .arch-item .small {
    display: block;
    font-size: 0.6875rem;
    font-weight: 400;
    color: #94a3b8;
    margin-top: 0.25rem;
  }
  .training-content .arch-connector {
    text-align: center;
    color: #4b5563;
    font-size: 1.25rem;
    line-height: 2;
  }

  /* ── Flow Diagram ────────────────────────────────── */
  .training-content .flow-diagram {
    margin: 1.5rem 0;
  }
  .training-content .flow-step {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .training-content .flow-num {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: rgba(99, 102, 241, 0.2);
    border: 1px solid rgba(99, 102, 241, 0.4);
    color: #a5b4fc;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    flex-shrink: 0;
  }
  .training-content .flow-box {
    flex: 1;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.8125rem;
    font-weight: 600;
    border: 1px solid;
  }
  .training-content .flow-box .small {
    display: block;
    font-size: 0.6875rem;
    font-weight: 400;
    opacity: 0.7;
    margin-top: 0.125rem;
  }
  .training-content .flow-box.blue {
    background: rgba(59, 130, 246, 0.15);
    border-color: rgba(59, 130, 246, 0.3);
    color: #93c5fd;
  }
  .training-content .flow-box.green {
    background: rgba(34, 197, 94, 0.15);
    border-color: rgba(34, 197, 94, 0.3);
    color: #86efac;
  }
  .training-content .flow-box.orange {
    background: rgba(245, 158, 11, 0.15);
    border-color: rgba(245, 158, 11, 0.3);
    color: #fcd34d;
  }
  .training-content .flow-box.indigo {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.3);
    color: #a5b4fc;
  }
  .training-content .flow-connector {
    text-align: center;
    color: #4b5563;
    font-size: 1rem;
    line-height: 1.5;
    margin-left: 0.875rem;
  }

  /* ── Tag Grid ────────────────────────────────────── */
  .training-content .tag-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 1rem 0;
  }
  .training-content .tag {
    background: rgba(99, 102, 241, 0.15);
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: #a5b4fc;
    padding: 0.25rem 0.625rem;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-family: 'Fira Code', 'Cascadia Code', monospace;
  }
`;
